import {Box, Button, Grid, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Container from "../../../common/Container";
import {FilterPanel} from './components'
import {AuctionType} from "../../../utils/enums";
import {PlansFilterParams, SearchFunction, PlanList} from "../../../types/plan";
import {useConfirmation} from "../../../context/ConfirmationModalProvider";
import {DataGrid} from "@mui/x-data-grid";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import {ActionColumn, Column, columnStyle, DateColumn, DateTimeColumn, GrossColumn} from "../../../common/Columns";
import {useSelector} from "react-redux";
import {State} from "../../../redux/store";
import {Company} from "../../../types/company";
import {planService} from "../../../api/services/plan.service";
import {useLocation, useNavigate} from "react-router-dom";
import {useSnack} from "../../../utils/useSnack";
import {PlanState} from "../../../utils/enums";
import BoxWithLabel from "../../../common/BoxWithLabel";

export default function Plans() {
    const {confirm} = useConfirmation();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {snack} = useSnack();

    const [plans, setPlans] = useState<PlanList[]>([]);
    const [isNewOpen, setIsNewOpen] = useState(false);
    const location = useLocation();

    const onOpenNew = () => setIsNewOpen(true);
    const onCloseNew = () => setIsNewOpen(false);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalElements, setTotalElements] = useState(0);
    const [auctionType, setAuctionType] = useState(AuctionType.tender);
    const [loading, setLoading] = useState(false);
    const state = useSelector((state: State) => state);
    const company: Company | null = state.company.company;

    const filterInitialState: PlansFilterParams = {year: null, state: null};

    const [filters, setFilters] = useState<PlansFilterParams>(filterInitialState);

    const actions = [
        {
            label: "action.navigate",
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                navigate(`/account-plan/${row.id}`);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return true;
            }
        },
        {
            label: "action.remove",
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                handleDeletePlan(row.id);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return row.state === PlanState.draft;
            }
        },
        {
            label: "action.approve",
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                handleApprovePlan(row.id);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return row.state === PlanState.draft;
            }
        }]


    async function getPlans(year: number | null, state: PlanState | null) {
        if (!company) return;
        try {
            setLoading(true);
            const response = await planService.getPlans(company.id, page, pageSize, year, state);
            if (response.status === "success" && response.data) {
                setPlans(response.data.data);
                setTotalElements(response.data.totalElements);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (company) getPlans(filters.year, filters.state);
    }, [company, filters, location.pathname, page, pageSize]);


    const search: SearchFunction<PlansFilterParams> = (filters: PlansFilterParams) => {
        setFilters(filters);
    };

    const handleCreate = async () => {
        const result = await confirm({
            title: t('cabinet.plans.confirm.create_plan.title'),
            message: t('cabinet.plans.confirm.create_plan.body')
        });

        if (result) {
            planService.createPlan(company!.id).then(async (response: any) => {
                if (response && response.data && response.status === "success") {
                    snack(t("snack.success.plan_created"), "success");
                    await getPlans(filters.year, filters.state);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            })
        } else {
            console.log("Удаление отменено!");
        }
    };

    const handleDeletePlan = async (planId: number) => {
        const result = await confirm({
            title: t('cabinet.plans.confirm.delete_plan.title'),
            message: t('cabinet.plans.confirm.delete_plan.body')
        });

        if (result) {
            planService.deletePlan(company!.id, planId).then(async (response: any) => {
                if (response && response.data && response.status === "success") {
                    snack(t("snack.success.plan_deleted"), "success");
                    await getPlans(filters.year, filters.state);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            })
        } else {
            console.log("Удаление отменено!");
        }
    }

    const handleApprovePlan = async (planId: number) => {
        const result = await confirm({
            title: t('cabinet.plans.confirm.approve_plan.title'),
            message: t('cabinet.plans.confirm.approve_plan.body')
        });

        if (result) {
            planService.approvePlan(company!.id, planId).then(async (response: any) => {
                if (response && response.data && response.status === "success") {
                    snack(t("snack.success.plan_approved"), "success");
                    await getPlans(filters.year, filters.state);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            })
        } else {
            console.log("Approval canceled!");
        }
    }

    return (
        <Box>
            <Container paddingY={{xs: 2, sm: 4}} maxWidth="unset">
                <Box marginBottom={4}>
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                        }}
                    >
                        {t("cabinet.plans.title")}
                    </Typography>
                </Box>


                <Grid container>
                    <Grid item xs={12} sm={7} md={8} lg={9}>
                        <BoxWithLabel label={t("common.filters")}>
                            <FilterPanel
                                onSearch={search}
                                t={t}/>
                        </BoxWithLabel>
                    </Grid>
                </Grid>


                <Grid item container spacing={2} alignItems={"center"} mb={2}>
                    <Grid item xs={12} sm={5} md={4} lg={3}>
                        <Button fullWidth color="primary" variant="contained"
                                onClick={handleCreate}>
							<span style={{whiteSpace: 'nowrap'}}>
								{t("cabinet.plans.create-procurement-plan")}
							</span>
                        </Button>
                    </Grid>
                </Grid>

                <Box>
                    <DataGrid
                        components={{NoRowsOverlay: () => <NoRowsOverlay/>}}
                        sx={{
                            height: 500,
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "pre-wrap"
                            },
                        }}
                        columnBuffer={4}
                        rows={plans.length
                            ? plans
                            : []
                        }
                        columns={[
                            ActionColumn(100, columnStyle, "cabinet.plans", actions),
                            Column("id", 100, columnStyle, undefined, "cabinet.plans"),
                            Column("year", 150, columnStyle, undefined, "cabinet.plans"),
                            DateTimeColumn("createdAt", 200, columnStyle, "cabinet.plans"),
                            DateTimeColumn("approvedAt", 200, columnStyle, "cabinet.plans"),
                            Column("itemCount", 100, columnStyle, undefined, "cabinet.plans"),
                            GrossColumn(200, columnStyle, "cabinet.plans"),
                            Column("state", 100, columnStyle, "plan_state", "cabinet.plans")
                        ]}
                        loading={loading}
                        density="comfortable" style={{whiteSpace: "pre-wrap"}}
                    />
                </Box>
            </Container>
        </Box>
    )
}
